import { Country } from '@shopify/address/build/ts/index';
import axios, { AxiosError } from 'axios';
import { FC, useEffect, useState } from 'react';
import { BrowserRouter, Route, Switch, useLocation } from 'react-router-dom';
import { CUSTOMER_API_URL } from './constants';
import { Address } from './pages/Address';
import { SubscriptionAdd } from './pages/SubscriptionAdd';
import { StatusMessage, SubscriptionDetail } from './pages/SubscriptionDetail';
import { SubscriptionLineEditProduct } from './pages/SubscriptionLineEditProduct';
import { SubscriptionLineEditVariant } from './pages/SubscriptionLineEditVariant';
import { SubscriptionList } from './pages/SubscriptionList';
import './assets/css/account.css';

export type Setting = {
  token: string;
  resize: (height: number) => void;
  referrer?: string;
  getScroll: () => { x: number; y: number };
  monitorScroll: () => void;
};

type SubscriptionStatus = 'ACTIVE' | 'PAUSED' | 'CANCELLED';

export type SubscriptionLine = {
  productId: string;
  variantId: string;
  variantImage?: string;
  title: string;
  onlineStorePreviewUrl: string;
  variantTitle: string;
  sku?: string;
  quantity: number;
  currentPriceAmount: number;
  currentPriceCurrencyCode: string;
  lineDiscountedPriceAmount: number;
  lineDiscountedPriceCurrencyCode: string;
  lineId: string;
  sellingPlanId?: string;
};

type BillingAttempt = {
  orderId?: string;
  orderToken?: string;
  orderName?: string;
  ready: boolean;
  createdAt: string;
  errorMessage?: string;
  errorCode?: string;
};

type SubscriptionHistory = {
  status: string;
  skipCount?: number;
  canceledAt: string;
  createdAt: string;
};

type RankGroup = {
  public: boolean;
};

export type Shop = {
  showPause: boolean;
  showCancel: boolean;
  showSkip: boolean;
  showChangeAddress: boolean;
  mypageRemarks: string;
  showDelivery: boolean;
  deliveryDays: number;
  mypageChangeDeliveryDays: boolean;
  enableDeliveryTimesAM: boolean;
  enableDeliveryTimes12: boolean;
  enableDeliveryTimes14: boolean;
  enableDeliveryTimes16: boolean;
  enableDeliveryTimes18: boolean;
  enableDeliveryTimes19: boolean;
  enableDeliveryTimes20: boolean;
  deliveryConvenience: string;
  disableDeliveryDaysMon: boolean;
  disableDeliveryDaysTue: boolean;
  disableDeliveryDaysWed: boolean;
  disableDeliveryDaysThr: boolean;
  disableDeliveryDaysFri: boolean;
  disableDeliveryDaysSat: boolean;
  disableDeliveryDaysSun: boolean;
  disableDeliveryDaysHoliday: boolean;
  showChangeProduct: boolean;
  showNewAddProduct: boolean;
  showChangePayment: boolean;
  showRemoveProduct: boolean;
  showNewOrder: boolean;
  rankGroup: RankGroup;
  showChangeQuantity: boolean;
  maxSkipCount: number;
  hideLowestBuySkip: boolean;
  hideLowestBuyPause: boolean;
  hideLowestBuyCancel: boolean;
  showPlanModify: boolean;
  isAllowModifyDays: boolean;
  allowModifyDays: number;
  hideLowestModify: boolean;
  showStockAlert: boolean;
  deliveryDaysMax: number;
  changeNextBillingDateWithDeliveryDate: boolean;
  enableDeliveryDateNoDay: boolean;
  deliveryTimes: { tag: string; title: string }[];
  businessHolidays: { targetDate: string }[];
};

type Rank = {
  name: string;
  discountPercentage: number;
};

export type Subscription = {
  customerId: string;
  originOrderId: string;
  originOrderToken: string;
  originOrderName: string;
  customerDisplayName: string;
  customerEmail: string;
  customerPaymentMethodId?: string;
  nextBillingDate: Date;
  subscriptionContractId: string;
  billingPolicyInterval: 'DAY' | 'WEEK' | 'MONTH' | 'YEAR';
  billingPolicyIntervalCount: number;
  billingPolicyMinCycles: number;
  billingPolicyMaxCycles: number;
  status: SubscriptionStatus;
  subscriptionLines: SubscriptionLine[];
  subscriptionHistories: SubscriptionHistory[];
  billingAttempts: BillingAttempt[];
  deliveryPriceAmount: number;
  deliveryPriceCurrencyCode: string;
  deliveryAddress1: string;
  deliveryAddress2: string;
  deliveryCity: string;
  deliveryCompany: string;
  deliveryCountry: string;
  deliveryCountryCode: string;
  deliveryFirstName: string;
  deliveryLastName: string;
  deliveryName: string;
  deliveryPhone: string;
  deliveryProvince: string;
  deliveryProvinceCode: string;
  deliveryZip: string;
  deliveryDays: number;
  deliveryTime: string;
  deliveryTimeText: string;
  createdAt: string;
  disabled: boolean;
  deliveryFormattedAddress: string[];
  countries: Country[];
  country: Country;
  paymentType: string;
  shop: Shop;
  rank: Rank;
};

export const statusMessages = {
  ACTIVE: 'アクティブ',
  PAUSED: '一時停止',
  CANCELLED: '解約',
  EXPIRED: '期限切れ',
  FAILED: '失敗',
};
const History: FC<{ children: any }> = ({ children }) => {
  const pathname = useLocation().pathname;
  useEffect(() => {
    window.parent.postMessage({ action: 'pushHistory', path: pathname }, '*');
    window.postMessage('stopMonitor', '*');
  }, [pathname]);

  return children;
};
/**
 * kanehori change code
 * 午前中：9
 * 14時〜16時：14
 * 16時〜18時：16
 * 18時〜20時：18
 * 19時〜21時：19
 */
export const deliveryTimesConstants: { value: string; label: string }[] = [
  { value: '9', label: '午前中' },
  { value: '12', label: '12時〜14時' },
  { value: '14', label: '14時〜16時' },
  { value: '16', label: '16時〜18時' },
  { value: '18', label: '18時〜20時' },
  { value: '19', label: '19時〜21時' },
  { value: '20', label: '20時〜21時' },
];

export const getPriceWithTax = (price) => {
  const taxRate = 1;
  const finalPrice = Math.floor(price * taxRate);

  return finalPrice.toLocaleString();
};

export const getOrderStatus = (subscription) => {
  let orderStatus = statusMessages[subscription.status as StatusMessage];
  if (orderStatus === 'アクティブ') orderStatus = 'お申込み中';

  return orderStatus;
};

export const getModalOption = (setting: Setting) => {
  return {
    disableFocus: true,
    onShow: () => {
      const dialog = document.querySelector('.micromodal.is-open [role="dialog"]') as HTMLDivElement;
      const body = document.querySelector('body');
      if (!dialog || !body) {
        return;
      }

      const scroll = setting.getScroll();
      let pos = scroll.y;

      if (body.clientHeight < pos + dialog.clientHeight) {
        pos = body.clientHeight - dialog.clientHeight - 10;
      } else if (pos < 10) {
        pos = 10;
      }

      dialog.style.top = `${pos}px`;
    },
  };
};

export const App = () => {
  const [config, setConfig] = useState<{
    token: string;
    styleTexts: string[];
    styleUrls: string[];
  }>();

  let scrollX = 0,
    scrollY = 0;

  const resize = (height: number) => {
    window.parent.postMessage({ action: 'resetWindow', height }, '*');
  };

  const getScroll = () => {
    return {
      x: scrollX,
      y: scrollY,
    };
  };

  let scrollTracking = false;
  let frameId = 0;
  const onScroll = async (e: MessageEvent) => {
    if (frameId && e.data === 'stopMonitor') {
      window.removeEventListener('message', onScroll);
      return;
    }

    if (scrollTracking || e.data.pageYOffset === undefined || e.data.pageXOffset === undefined) {
      return;
    }

    if (frameId) {
      cancelAnimationFrame(frameId);
      frameId = 0;
      scrollTracking = false;
    }

    frameId = requestAnimationFrame(() => {
      scrollX = e.data.pageXOffset;
      scrollY = e.data.pageYOffset;

      scrollTracking = false;
    });

    scrollTracking = true;
  };

  const monitorScroll = () => {
    window.addEventListener('message', onScroll);
  };

  // Ref: https://github.com/mdn/translated-content/blob/main/files/ja/web/api/web_storage_api/using_the_web_storage_api/index.html#L54
  const storageAvailable = () => {
    var storage;
    try {
      storage = window.localStorage;
      var x = '__storage_test__';
      storage.setItem(x, x);
      storage.removeItem(x);
      return true;
    } catch (e) {
      return (
        e instanceof DOMException &&
        // everything except Firefox
        (e.code === 22 ||
          // Firefox
          e.code === 1014 ||
          // test name field too, because code might not be present
          // everything except Firefox
          e.name === 'QuotaExceededError' ||
          // Firefox
          e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
        // acknowledge QuotaExceededError only if there's something already stored
        storage &&
        storage.length !== 0
      );
    }
  };

  const loggedIn = (shopId: string) => {
    const shopTokenStr = localStorage.getItem(shopId);
    if (!shopTokenStr) return false;

    const shopToken = JSON.parse(shopTokenStr);
    return (
      shopToken.jwt && shopToken.jwtExpiredTimestamp && Number(shopToken.jwtExpiredTimestamp) >= new Date().getTime()
    );
  };

  const initializeEvent = (event: MessageEvent) => {
    // We can't rejected by event.origin because Shopify Store domain is not unique.
    // So, We check data structure only.
    const { code, styleTexts, styleUrls, shopId } = event.data;
    if (code === undefined || !styleTexts || !styleUrls || !shopId) {
      return;
    }

    if (storageAvailable() && loggedIn(shopId)) {
      const shopTokenStr = localStorage.getItem(shopId);
      const shopToken = JSON.parse(shopTokenStr!);

      setConfig({ token: shopToken.jwt, styleTexts, styleUrls });

      return;
    }

    axios
      .post(`${CUSTOMER_API_URL}?action=token`, {
        code,
      })
      .then((response) => {
        window.removeEventListener('message', initializeEvent);

        const token = response.data;
        const jwtExpiredTimestamp = new Date().getTime() + 60 * 60 * 1000; // 1 hour

        if (storageAvailable()) {
          const shopToken = {
            jwt: token,
            jwtExpiredTimestamp: jwtExpiredTimestamp,
          };
          localStorage.setItem(shopId, JSON.stringify(shopToken));
        }

        setConfig({ token, styleTexts, styleUrls });
      })
      .catch((error: AxiosError) => {
        if (!error.response) {
          // Debug for now
          throw new Error(`Network Error but not response: ${error.message}`);
        }

        if (error.response.status >= 500) {
          throw error;
        }
      });
  };

  useEffect(() => {
    window.addEventListener('message', initializeEvent);

    window.addEventListener('resize', () => {
      // Safari doesn't compute "float: left" height.
      if (!document.querySelector('body')!.scrollHeight) {
        return;
      }

      const height = document.querySelector('body')!.scrollHeight + 100; // buffer;

      resize(height);
    });

    window.parent.postMessage({ action: 'initialize' }, '*');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!config) {
    return <>認証中です。少々お待ちください。</>;
  }

  const setting = {
    token: config.token,
    resize,
    referrer: document.referrer,
    getScroll,
    monitorScroll,
  };

  config.styleTexts.forEach((styleText) => {
    const style = document.createElement('style');
    style.innerText = styleText;

    document.body.appendChild(style);
  });

  config.styleUrls.forEach((url) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;

    document.body.appendChild(link);
  });

  return (
    <BrowserRouter basename="/?path=">
      <History>
        <Switch>
          <Route path="/subscription/add">
            <SubscriptionAdd setting={setting} />
          </Route>
          <Route path="/subscriptions/:id/products/:productId/lines/:lineId">
            <SubscriptionLineEditVariant setting={setting} />
          </Route>
          <Route path="/subscriptions/:id/lines/:lineId">
            <SubscriptionLineEditProduct setting={setting} />
          </Route>
          <Route path="/subscriptions/:id/edit">
            <Address setting={setting} />
          </Route>
          <Route path="/subscriptions/:id">
            <SubscriptionDetail setting={setting} />
          </Route>
          <Route path="/">
            <SubscriptionList setting={setting} />
          </Route>
        </Switch>
      </History>
    </BrowserRouter>
  );
};
